import { Component } from "@builder.io/sdk";

export const headerBlogsEventsClassesConfig: Component = {
  name: "Header Blogs Events Classes",
  inputs: [
    {
      name: "id",
      type: "string",
      defaultValue: "header-blogs-events-classes",
      friendlyName: "Section ID",
      helperText:
        "Unique identifier for the section. Useful for linking to the section by '#id'.",
      advanced: true,
    },
    {
      name: "type",
      type: "string",
      enum: ["search", "one-CTA", "two-CTA"],
      defaultValue: "one-CTA",
      friendlyName: "Header Type",
      helperText:
        "Type of header to display: search (with search bar), one-CTA (single call-to-action), or two-CTA (primary and secondary buttons)",
      required: true,
    },
    {
      name: "asset",
      type: "string",
      enum: ["image", "illustration"],
      defaultValue: "image",
      friendlyName: "Visual Asset Type",
      helperText:
        "Type of visual content: image (photo/banner) or illustration (custom graphic)",
      required: true,
    },
    {
      name: "heading",
      type: "string",
      defaultValue: "Transforming Elder Care with CI-VR Technology",
      friendlyName: "Main Heading",
      helperText:
        "Primary headline that captures attention and communicates the main value proposition",
      required: true,
    },
    {
      name: "subheading",
      type: "longText",
      defaultValue:
        "This innovative VR training program offers immersive, hands-on experiences, simulating real-life scenarios for Alzheimer's and dementia care.",
      friendlyName: "Subheading/Description",
      helperText:
        "Supporting text that provides more details about the offering or program",
      required: true,
    },
    {
      name: "primaryCta",
      type: "object",
      friendlyName: "Primary Call-to-Action",
      defaultValue: {
        text: "Request a Demo",
        href: "#inquiry-ci-vr",
      },
      subFields: [
        {
          name: "text",
          type: "string",
          defaultValue: "Request a Demo",
          friendlyName: "Button Text",
          helperText:
            "Text displayed on the primary action button (e.g., 'Request a Demo', 'Get Started')",
          required: true,
        },
        {
          name: "href",
          type: "string",
          defaultValue: "#inquiry-ci-vr",
          friendlyName: "Button Link",
          helperText:
            "URL or anchor link for the button (use '#section-id' for page anchors or full URLs for external links)",
          required: true,
        },
      ],
      helperText: "Main action button that drives user engagement",
      required: true,
      showIf:
        "options.get('type') === 'one-CTA' || options.get('type') === 'two-CTA'",
    },
    {
      name: "secondaryCta",
      type: "object",
      friendlyName: "Secondary Call-to-Action",
      defaultValue: {
        text: "Learn More",
        href: "#features",
      },
      subFields: [
        {
          name: "text",
          type: "string",
          defaultValue: "Learn More",
          friendlyName: "Button Text",
          helperText:
            "Text for the secondary action button (usually a softer action like 'Learn More')",
          required: true,
        },
        {
          name: "href",
          type: "string",
          defaultValue: "#features",
          friendlyName: "Button Link",
          helperText: "URL or anchor link for the secondary button",
          required: true,
        },
      ],
      helperText:
        "Secondary action button for users who want to explore before committing",
      required: true,
      showIf: "options.get('type') === 'two-CTA'",
    },
    {
      name: "image",
      type: "object",
      friendlyName: "Header Image",
      defaultValue: {
        src: "https://cdn.builder.io/api/v1/image/assets%2Fad5264505ff74b8ba929a635f7c3c34f%2F6e77b67d0fd44795843e63cc33d473ea",
        alt: "CI-VR Training Technology",
      },
      subFields: [
        {
          name: "src",
          type: "file",
          allowedFileTypes: ["jpeg", "jpg", "png", "svg", "webp"],
          friendlyName: "Image File",
          helperText:
            "Upload an image that represents your VR training program or technology",
          required: true,
        },
        {
          name: "alt",
          type: "string",
          friendlyName: "Alt Text",
          helperText:
            "Descriptive text for screen readers and accessibility (describe what's in the image)",
          required: true,
        },
      ],
      helperText:
        "Hero image that visually represents your VR training program",
      required: true,
      showIf: "options.get('asset') === 'image'",
    },
    {
      name: "illustrationType",
      type: "string",
      defaultValue: "BlogsIllustration",
      friendlyName: "Illustration Type",
      enum: ["BlogsIllustration"],
      helperText:
        "Select the type of illustration to display (currently supports BlogsIllustration)",
      required: true,
      showIf: "options.get('asset') === 'illustration'",
    },
    {
      name: "backgroundColor",
      type: "string",
      friendlyName: "Background Color",
      defaultValue: "primary-100",
      enum: ["white", "primary-100", "secondary-100"],
      helperText:
        "Background color for the header section: white, primary-100 (Light Primary), or secondary-100 (Light Secondary)",
      advanced: true,
    },
    {
      name: "textAlignment",
      type: "string",
      friendlyName: "Text Alignment",
      defaultValue: "left",
      enum: ["left", "center"],
      helperText:
        "Alignment of the header text content: left (default) or center",
      advanced: true,
    },
    {
      name: "contentOrder",
      type: "string",
      friendlyName: "Content Order",
      defaultValue: "text-first",
      enum: ["text-first", "image-first"],
      helperText:
        "Order of content on mobile: text-first (text above image) or image-first (image above text)",
      advanced: true,
    },
  ],
};
